import React from 'react';
import {
  Skeleton,
  Stack,
  Card,
  Group,
  Table,
  SimpleGrid,
  Center,
  Loader,
  Text,
  Paper,
  Title,
  Progress,
  ThemeIcon,
  Timeline,
} from '@mantine/core';
import { IconActivity, IconAlertTriangle } from '@tabler/icons-react';

// Generic loading skeleton for cards
export const CardSkeleton: React.FC<{ height?: number }> = ({ height = 120 }) => (
  <Card withBorder>
    <Stack gap="md">
      <Group justify="space-between">
        <Skeleton height={20} width="60%" />
        <Skeleton height={16} width={16} radius="xl" />
      </Group>
      <Skeleton height={height - 60} />
      <Group gap="xs">
        <Skeleton height={12} width="30%" />
        <Skeleton height={12} width="20%" />
      </Group>
    </Stack>
  </Card>
);

// Table loading skeleton
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({ 
  rows = 5, 
  columns = 6 
}) => (
  <Table>
    <Table.Thead>
      <Table.Tr>
        {Array.from({ length: columns }).map((_, i) => (
          <Table.Th key={i}>
            <Skeleton height={16} width={Math.random() * 60 + 40 + '%'} />
          </Table.Th>
        ))}
      </Table.Tr>
    </Table.Thead>
    <Table.Tbody>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Table.Tr key={rowIndex}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Table.Td key={colIndex}>
              <Skeleton height={14} width={Math.random() * 40 + 30 + '%'} />
            </Table.Td>
          ))}
        </Table.Tr>
      ))}
    </Table.Tbody>
  </Table>
);

// Dashboard metrics skeleton
export const MetricsSkeleton: React.FC = () => (
  <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
    {Array.from({ length: 4 }).map((_, i) => (
      <Card key={i} withBorder>
        <Group>
          <Skeleton height={40} width={40} radius="md" />
          <div style={{ flex: 1 }}>
            <Skeleton height={12} width="60%" mb="xs" />
            <Skeleton height={24} width="40%" mb="xs" />
            <Skeleton height={10} width="30%" />
          </div>
        </Group>
      </Card>
    ))}
  </SimpleGrid>
);

// Timeline skeleton for activities
export const TimelineSkeleton: React.FC<{ items?: number }> = ({ items = 5 }) => (
  <Timeline active={items} bulletSize={24} lineWidth={2}>
    {Array.from({ length: items }).map((_, i) => (
      <Timeline.Item
        key={i}
        bullet={<Skeleton height={16} width={16} radius="xl" />}
        title={<Skeleton height={16} width="40%" />}
      >
        <Skeleton height={12} width="80%" mb="xs" />
        <Skeleton height={10} width="30%" />
      </Timeline.Item>
    ))}
  </Timeline>
);

// Student list skeleton
export const StudentListSkeleton: React.FC = () => (
  <Stack gap="md">
    <Group justify="space-between">
      <Skeleton height={32} width="200px" />
      <Skeleton height={36} width="120px" />
    </Group>
    <Card>
      <Stack gap="md">
        <Group>
          <Skeleton height={36} width="300px" />
          <Skeleton height={36} width="150px" />
          <Skeleton height={36} width="150px" />
        </Group>
      </Stack>
    </Card>
    <Paper withBorder>
      <TableSkeleton rows={10} columns={7} />
    </Paper>
  </Stack>
);

// Camera grid skeleton
export const CameraGridSkeleton: React.FC = () => (
  <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
    {Array.from({ length: 6 }).map((_, i) => (
      <Card key={i} withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap="sm">
              <Skeleton height={24} width={24} radius="sm" />
              <Skeleton height={16} width="120px" />
            </Group>
            <Skeleton height={20} width={60} radius="xl" />
          </Group>
          <Skeleton height={100} radius="md" />
          <Group justify="space-between">
            <Skeleton height={12} width="40%" />
            <Skeleton height={12} width="30%" />
          </Group>
          <Group>
            <Skeleton height={28} width={60} radius="sm" />
            <Skeleton height={28} width={60} radius="sm" />
            <Skeleton height={28} width={60} radius="sm" />
          </Group>
        </Stack>
      </Card>
    ))}
  </SimpleGrid>
);

// Activity feed skeleton
export const ActivityFeedSkeleton: React.FC = () => (
  <Stack gap="md">
    {Array.from({ length: 8 }).map((_, i) => (
      <Card key={i} withBorder>
        <Group justify="space-between">
          <Group gap="md">
            <Skeleton height={32} width={32} radius="xl" />
            <div>
              <Skeleton height={14} width="150px" mb="xs" />
              <Group gap="xs">
                <Skeleton height={18} width={60} radius="xl" />
                <Skeleton height={18} width={80} radius="xl" />
              </Group>
            </div>
          </Group>
          <Group gap="sm">
            <Skeleton height={12} width="80px" />
            <Skeleton height={24} width={24} radius="sm" />
          </Group>
        </Group>
      </Card>
    ))}
  </Stack>
);

// Chart skeleton
export const ChartSkeleton: React.FC<{ height?: number }> = ({ height = 300 }) => (
  <Card>
    <Group justify="space-between" mb="md">
      <Skeleton height={20} width="200px" />
      <Group gap="xs">
        <Skeleton height={16} width={60} radius="xl" />
        <Skeleton height={16} width={80} radius="xl" />
      </Group>
    </Group>
    <Skeleton height={height} radius="md" />
  </Card>
);

// Alert list skeleton
export const AlertListSkeleton: React.FC = () => (
  <Stack gap="md">
    {Array.from({ length: 6 }).map((_, i) => (
      <Card key={i} withBorder>
        <Group justify="space-between">
          <Group gap="md">
            <Skeleton height={32} width={32} radius="md" />
            <div style={{ flex: 1 }}>
              <Skeleton height={16} width="70%" mb="xs" />
              <Skeleton height={12} width="90%" mb="xs" />
              <Group gap="xs">
                <Skeleton height={16} width={60} radius="xl" />
                <Skeleton height={16} width={80} radius="xl" />
              </Group>
            </div>
          </Group>
          <Group gap="sm">
            <Skeleton height={24} width={70} radius="xl" />
            <Skeleton height={28} width={28} radius="sm" />
          </Group>
        </Group>
      </Card>
    ))}
  </Stack>
);

// Full page loading with message
export const FullPageLoader: React.FC<{ message?: string }> = ({ 
  message = "Loading..." 
}) => (
  <Center style={{ height: '60vh' }}>
    <Stack align="center" gap="md">
      <Loader size="lg" />
      <Text size="sm" c="dimmed">{message}</Text>
    </Stack>
  </Center>
);

// Inline loader for smaller components
export const InlineLoader: React.FC<{ message?: string; size?: 'xs' | 'sm' | 'md' | 'lg' }> = ({ 
  message, 
  size = 'sm' 
}) => (
  <Group justify="center" p="md">
    <Loader size={size} />
    {message && <Text size="sm" c="dimmed">{message}</Text>}
  </Group>
);

// Progress loader with percentage
export const ProgressLoader: React.FC<{ 
  value: number; 
  label?: string;
  color?: string;
}> = ({ value, label, color = 'blue' }) => (
  <Stack gap="xs">
    {label && <Text size="sm" fw={500}>{label}</Text>}
    <Progress value={value} color={color} size="md" radius="xl" />
    <Text size="xs" c="dimmed" ta="center">{value}%</Text>
  </Stack>
);

// Loading state for specific sections
export const DashboardSkeleton: React.FC = () => (
  <Stack gap="lg">
    {/* Header */}
    <Group justify="space-between">
      <div>
        <Skeleton height={32} width="300px" mb="xs" />
        <Skeleton height={16} width="500px" />
      </div>
      <Group>
        <Skeleton height={36} width="100px" />
        <Skeleton height={36} width="120px" />
      </Group>
    </Group>

    {/* Metrics */}
    <MetricsSkeleton />

    {/* Main content */}
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
      <ChartSkeleton height={350} />
      <Card>
        <Skeleton height={20} width="200px" mb="md" />
        <TimelineSkeleton items={6} />
      </Card>
    </SimpleGrid>
  </Stack>
);

// Smart loading component that shows different states
export const SmartLoader: React.FC<{
  isLoading: boolean;
  error?: Error | null;
  isEmpty?: boolean;
  children: React.ReactNode;
  skeleton?: React.ReactNode;
  emptyState?: React.ReactNode;
  errorState?: React.ReactNode;
}> = ({ 
  isLoading, 
  error, 
  isEmpty, 
  children, 
  skeleton,
  emptyState,
  errorState 
}) => {
  if (isLoading) {
    return skeleton ? <>{skeleton}</> : <InlineLoader message="Loading data..." />;
  }

  if (error) {
    return errorState ? <>{errorState}</> : (
      <Card withBorder>
        <Center p="xl">
          <Stack align="center" gap="md">
            <ThemeIcon size="xl" color="red" variant="light">
              <IconAlertTriangle size={24} />
            </ThemeIcon>
            <div style={{ textAlign: 'center' }}>
              <Title order={4} c="red" mb="xs">Error Loading Data</Title>
              <Text size="sm" c="dimmed">{error.message}</Text>
            </div>
          </Stack>
        </Center>
      </Card>
    );
  }

  if (isEmpty) {
    return emptyState ? <>{emptyState}</> : (
      <Card withBorder>
        <Center p="xl">
          <Stack align="center" gap="md">
            <ThemeIcon size="xl" color="gray" variant="light">
              <IconActivity size={24} />
            </ThemeIcon>
            <div style={{ textAlign: 'center' }}>
              <Title order={4} c="dimmed" mb="xs">No Data Available</Title>
              <Text size="sm" c="dimmed">There's no data to display at the moment.</Text>
            </div>
          </Stack>
        </Center>
      </Card>
    );
  }

  return <>{children}</>;
};

export default {
  CardSkeleton,
  TableSkeleton,
  MetricsSkeleton,
  TimelineSkeleton,
  StudentListSkeleton,
  CameraGridSkeleton,
  ActivityFeedSkeleton,
  ChartSkeleton,
  AlertListSkeleton,
  FullPageLoader,
  InlineLoader,
  ProgressLoader,
  DashboardSkeleton,
  SmartLoader,
};